/**
 * THE WORDS A FAMILY OR A DANCER READS ABOUT HER OWN COSTUME BAG (2026-09-26).
 * Bobbie: "marks of on the dancers view os costumes are still waiting to be
 * picked up and what has been pciked up".
 *
 * One dancer, one line, four answers:
 *   Waiting for pickup        the studio has it at the desk and her bag has
 *                             not gone home yet;
 *   Picked up Sep 25          her own bag went home, dated in studio time
 *                             (America/Chicago), whatever the phone's zone;
 *   Not ready yet             the studio is still getting it in;
 *   You provide               the family finds this costume.
 * plus "Check with the studio" for a card that is not set up for pickup.
 *
 * The dancer's home (DancerCostumeCard), the class page (CostumePanel) and
 * the family dancer profile all read these, so a parent and her dancer never
 * see two different sentences about one bag. The reading of the record itself
 * is pickupStateFor in lib/costumePickupState.ts; this file only words it.
 *
 * PURE. No React, no Firebase, and only .ts-suffixed local imports, so Node's
 * type stripping runs it directly in tests.
 */
import type { CostumePickupState } from './costumePickupState.ts';
import { studioStamp } from './costumeTrail.ts';

export type CostumePickupKind = CostumePickupState['kind'];

/** How a line should look: done, waiting at the desk, not in yet, or neutral. */
export type CostumePickupTone = 'done' | 'waiting' | 'not-ready' | 'quiet';

export interface CostumePickupWords {
  kind: CostumePickupKind;
  text: string;
  tone: CostumePickupTone;
}

/**
 * Milliseconds from whatever a pickup time arrives as: a Firestore Timestamp
 * (toMillis), a Date, an ISO string from the dancer portal, a number, or a
 * plain {seconds} object. Anything else is null, never NaN and never a throw.
 */
export function pickupMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object') {
    const record = value as { toMillis?: unknown; seconds?: unknown };
    if (typeof record.toMillis === 'function') {
      try {
        const ms = (record.toMillis as () => number)();
        return Number.isFinite(ms) ? ms : null;
      } catch {
        return null;
      }
    }
    if (typeof record.seconds === 'number' && Number.isFinite(record.seconds)) return record.seconds * 1000;
  }
  return null;
}

/** "Sep 25", always the studio's own day (America/Chicago). */
export function pickupDayLabel(ms: number | null): string | null {
  if (ms === null) return null;
  try {
    return new Date(ms).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

/**
 * "Fri Sep 25, 6:42pm", studio time, for staff screens that need the hour
 * too. The SAME formatter as the report and the tracker (studioStamp in
 * lib/costumeTrail.ts), so a pickup reads one way on every staff screen
 * (design review E6, 2026-11-03).
 */
export function pickupStampLabel(ms: number | null): string | null {
  if (ms === null) return null;
  try {
    return studioStamp(ms) || null;
  } catch {
    return null;
  }
}

/**
 * ONE WORD SET FOR ONE BAG, ON EVERY SCREEN (design review, 2026-11-03).
 * The same state had five names ("Ready for pickup", "Ready at the front
 * desk, waiting for pickup", "Ready, needs pickup", "Waiting at the desk",
 * "Ready for pickup at the front desk"). Every screen now says these words,
 * staff and families alike. Only the two answers that speak TO someone
 * change with who reads them: a family reads "You provide", staff read
 * "Family provides".
 *
 * "Ready for pickup" stays the name of the STEP Kim presses (Mark ready for
 * pickup); a bag in that state reads "Waiting for pickup".
 */
export type CostumeWordsAudience = 'family' | 'staff';

export const COSTUME_PICKUP_STATE_TEXT = {
  'picked-up': 'Picked up',
  ready: 'Waiting for pickup',
  'not-ready': 'Not ready yet',
} as const;

/** The state's words alone, no date: chips, filters, counts. */
export function costumePickupStateText(kind: CostumePickupKind, audience: CostumeWordsAudience = 'family'): string {
  switch (kind) {
    case 'picked-up':
    case 'ready':
    case 'not-ready':
      return COSTUME_PICKUP_STATE_TEXT[kind];
    case 'family-provides':
      return audience === 'staff' ? 'Family provides' : 'You provide';
    default:
      return audience === 'staff' ? 'Not set up for pickup' : 'Check with the studio';
  }
}

/**
 * The colour of a status chip (components/CostumeStatusChip.tsx). RED MEANS
 * ACT NOW (design review A3, DESIGN 1B): for staff, only a bag waiting at the
 * desk. A family's waiting bag is good news, so it is green. Picked up is
 * done. Every other not-yet is the quiet neutral, and the words are always on
 * the chip, so colour is never the only signal.
 */
export type CostumeChipTone = 'alert' | 'good' | 'done' | 'quiet';

export function costumePickupChipTone(kind: CostumePickupKind, audience: CostumeWordsAudience = 'family'): CostumeChipTone {
  if (kind === 'ready') return audience === 'staff' ? 'alert' : 'good';
  if (kind === 'picked-up') return 'done';
  return 'quiet';
}

/** The one sentence for one dancer's bag, from its kind and (if picked up) when. */
export function costumePickupWords(kind: CostumePickupKind, at: unknown = null, audience: CostumeWordsAudience = 'family'): CostumePickupWords {
  switch (kind) {
    case 'picked-up': {
      const day = pickupDayLabel(pickupMillis(at));
      return { kind, text: day ? `${COSTUME_PICKUP_STATE_TEXT['picked-up']} ${day}` : COSTUME_PICKUP_STATE_TEXT['picked-up'], tone: 'done' };
    }
    case 'ready':
      return { kind, text: costumePickupStateText(kind, audience), tone: 'waiting' };
    case 'not-ready':
      return { kind, text: costumePickupStateText(kind, audience), tone: 'not-ready' };
    case 'family-provides':
      return { kind, text: costumePickupStateText(kind, audience), tone: 'quiet' };
    default:
      return { kind: 'check-with-studio', text: costumePickupStateText('check-with-studio', audience), tone: 'quiet' };
  }
}

/** Same, straight from pickupStateFor's answer. */
export function costumePickupWordsFor(state: CostumePickupState): CostumePickupWords {
  return costumePickupWords(state.kind, state.kind === 'picked-up' ? state.at : null);
}

const KINDS: readonly CostumePickupKind[] = ['picked-up', 'ready', 'not-ready', 'family-provides', 'check-with-studio'];

/** A kind as it crosses the wire (the dancer portal), or null if it is not one. */
export function asCostumePickupKind(value: unknown): CostumePickupKind | null {
  return typeof value === 'string' && (KINDS as readonly string[]).includes(value) ? (value as CostumePickupKind) : null;
}
